// services/config.ts
import i18n from '../i18n';
import { showMessage } from '../showMessage';
import { applyThemePreference } from '../theme';
import {
  applyMapColorModePreference,
  normalizeMapColorMode,
  type MapColorMode,
} from '../utils/mapboxStyle';

const NOTIFICATIONS_ERROR_KEY = 'notifications.error';
const ERRORS_MISSING_TOKEN_KEY = 'errors.missingToken';
const ERRORS_MISSING_API_BASE_KEY = 'errors.missingApiBase';
const ERRORS_FAILED_LOAD_SETTINGS_KEY = 'errors.failedLoadSettings';
const ERRORS_FAILED_ADMIN_CHECK_KEY = 'errors.failedAdminCheck';

interface RawUserSettingsResponse {
  success: boolean;
  settings?: {
    theme?: unknown;
    language?: unknown;
    default_boat_id?: unknown;
    share_data?: unknown;
    allow_analytics?: unknown;
    map_color_mode?: unknown;
  };
}

interface MappedAppConfig {
  theme: 'light' | 'dark' | 'system';
  language: 'en' | 'es' | 'it';
  default_boat_id: string;
  share_data: boolean;
  allow_analytics: boolean;
  map_color_mode: MapColorMode;
}

export const fetchAppConfig = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_MISSING_TOKEN_KEY));
      return null;
    }
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!apiBase) {
      showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_MISSING_API_BASE_KEY));
      return null;
    }
    const response = await fetch(`${apiBase}/api/user/getUserSettings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const json: unknown = await response.json();
    const data = json && typeof json === 'object' ? (json as Partial<RawUserSettingsResponse>) : {};
    if (data?.success !== true || !data.settings || typeof data.settings !== 'object') {
      return null;
    }

    const settings = data.settings as Record<string, unknown>;

    const themeRaw = settings.theme;
    const theme: MappedAppConfig['theme'] =
      themeRaw === '00' ? 'light' : themeRaw === '01' ? 'dark' : 'system';

    const rawLanguage =
      typeof settings.language === 'string' ? settings.language.toLowerCase() : 'en';
    const language: MappedAppConfig['language'] =
      rawLanguage === 'es' ? 'es' : rawLanguage === 'it' ? 'it' : 'en';

    const defaultBoat = settings.default_boat_id;
    const default_boat_id =
      typeof defaultBoat === 'string'
        ? defaultBoat
        : typeof defaultBoat === 'number'
          ? String(defaultBoat)
          : '';

    const share_data = settings.share_data === true;
    const allow_analytics = settings.allow_analytics === true;
    const map_color_mode = normalizeMapColorMode(settings.map_color_mode);

    const mappedConfig: MappedAppConfig = {
      theme,
      language,
      default_boat_id,
      share_data,
      allow_analytics,
      map_color_mode,
    };
    localStorage.setItem('appConfig', JSON.stringify(mappedConfig));
    applyThemePreference(mappedConfig.theme);
    applyMapColorModePreference(mappedConfig.map_color_mode);
    return mappedConfig;
  } catch (error) {
    console.error('Error fetching app config:', error);
    showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_FAILED_LOAD_SETTINGS_KEY));
    return null;
  }
};

export const checkIsAdmin = async (): Promise<boolean> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_MISSING_TOKEN_KEY));
      return false;
    }

    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!apiBase) {
      showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_MISSING_API_BASE_KEY));
      return false;
    }

    const response = await fetch(`${apiBase}/api/user/isAdmin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const json: unknown = await response.json();
    const data =
      json && typeof json === 'object' ? (json as { success?: unknown; isAdmin?: unknown }) : {};
    if (data.success !== true) {
      return false;
    }

    return data.isAdmin === true;
  } catch (error) {
    console.error('Error al verificar estado de administrador:', error);
    showMessage('error', i18n.t(NOTIFICATIONS_ERROR_KEY), i18n.t(ERRORS_FAILED_ADMIN_CHECK_KEY));
    return false;
  }
};
